<template>
  <div class="navarea-table-container">
    <!-- Фильтр для отображения только объектов по маршруту -->
    <div class="navarea-filter-controls mb-2">
      <div class="d-flex align-items-center">
        <span class="filter-label mr-2">Navarea along route</span>
        <b-form-input
          v-model.number="bufferDistanceNm"
          type="range"
          min="0"
          max="20"
          step="0.5"
          :disabled="!hasRouteData"
          class="navarea-distance-slider"
        />
        <span class="ml-2 small text-muted">
          ± {{ bufferDistanceDisplay }} NM
        </span>
      </div>
      <small v-if="!hasRouteData" class="text-muted ml-2">
        (No route data available)
      </small>
      <small v-else-if="isFilteringActive" class="text-info ml-2">
        Selected {{ filteredNavarea.length }} of {{ allNavareMessages.length }} items (± {{ effectiveBufferDistanceDisplay }} NM)
      </small>
      <small v-else class="text-muted ml-2">
        Set distance above 0&nbsp;NM to filter by route
      </small>
    </div>
    
    <b-table
        v-if="filteredNavarea.length"
        sticky-header
        size="sm"
        fixed
        striped
        hover
        responsive
        :items="formattedNavAreaData"
        :fields="fields"
        class="navarea-data-table"
        @row-clicked="handleRowClick"
        :tbody-tr-class="getRowClass"
        primary-key="uniqueKey"
    >
      <template #cell(content)="row">
        <div v-if="row.item.expanded">
          {{ row.item.content }}
          <b-button size="sm" variant="link" @click.stop="collapseRow(row.item.number)">
            less
          </b-button>
        </div>
        <div v-else>
          {{ truncateMessage(row.item.content) }}
          <b-button v-if="row.item.content.length > 100" size="sm" variant="link" @click.stop="expandRow(row.item.number)">
            more
          </b-button>
        </div>
      </template>
    </b-table>
    <div v-else-if="allNavareMessages.length" class="text-center text-muted py-3">
      No Navarea objects found along the route
    </div>
    <div v-else class="text-center text-muted py-3">
      No Navarea data available
    </div>
  </div>
</template>

<script>
import { useFleetStore } from '@/stores/fleet';
import { useRoutesStore } from '@/stores/routes';
import { mapState } from 'pinia';
import { eventBus } from '@/main';
import * as turf from '@turf/turf';

export default {
  name: "NavareaTable",  data() {
    return {
      expandedRowId: null,      
      selectedItemId: null,
      routeIntersectionCache: new Map(),
      isComponentActive: false,
      bufferDistanceNm: 0,
      effectiveBufferDistanceNm: 0,
      bufferDebounceTimer: null,
      calculationTimer: null,
      cacheClearTimeout: null,
      cachedRouteBounds: null,
      lastRouteHash: null,
      isCalculatingIntersections: false,
      fields: [
        { key: 'number', label: 'Number', sortable: true, thStyle: { width: '25%' }  },
        { key: 'country', label: 'Country', sortable: true, thStyle: { width: '20%' } },
        { key: 'content', label: 'Message', sortable: true },
      ]
    }
  },  
  mounted() {
    console.log("111 - Navarea")
    // Помечаем компонент как активный
    this.isComponentActive = true;
    
    // Слушаем клики по Navarea объектам на карте
    eventBus.$on('highlight-navarea-item-in-table', this.highlightItem);
  },
  // При активации компонента (если используется keep-alive) НЕ включаем Navarea слой автоматически
  
  // Слой должен включаться только при клике на таб
  activated() {
    this.isComponentActive = true;
  },
  // При деактивации компонента (если используется keep-alive) выключаем Navarea слой
  deactivated() {
    eventBus.$emit('toggle-navarea-layer', false);
    this.isComponentActive = false;
  
    // Отменяем pending debounce при деактивации
    if (this.cacheClearTimeout) {
      clearTimeout(this.cacheClearTimeout);
      this.cacheClearTimeout = null;
    }
    if (this.bufferDebounceTimer) {
      clearTimeout(this.bufferDebounceTimer);
      this.bufferDebounceTimer = null;
    }
  },  
  // Перед удалением компонента выключаем Navarea слой
  beforeDestroy() {
    eventBus.$emit('toggle-navarea-layer', false);
    this.isComponentActive = false;
    
    // Очищаем таймер при уничтожении компонента
    if (this.cacheClearTimeout) {
      clearTimeout(this.cacheClearTimeout);
      this.cacheClearTimeout = null;
    }
    if (this.bufferDebounceTimer) {
      clearTimeout(this.bufferDebounceTimer);
      this.bufferDebounceTimer = null;
    }
  },
  destroyed() {
    eventBus.$emit('toggle-navarea-layer', false);    // Отписываемся от событий по кликам
    eventBus.$off('highlight-navarea-item-in-table', this.highlightItem);
    
    // Очищаем таймеры
    if (this.calculationTimer) {
      clearTimeout(this.calculationTimer);
    }
    if (this.cacheClearTimeout) {
      clearTimeout(this.cacheClearTimeout);
    }
    if (this.bufferDebounceTimer) {
      clearTimeout(this.bufferDebounceTimer);
      this.bufferDebounceTimer = null;
    }
  },
  watch: {
    // Очищаем кэш при изменении маршрута, но только если это необходимо
    routeWaypoints: {
      handler() {
        // Очищаем кэш только если включена фильтрация и компонент активен (не скрыт в неактивной вкладке)
        if (this.isFilteringActive && this.isComponentActive) {
          // Добавляем debounce для избежания множественных очисток при быстрых изменениях маршрута
          this.debounceCacheClear();
        }
      },
      deep: true
    },
    bufferDistanceNm(newVal) {
      this.scheduleBufferUpdate(newVal);
    },
    // Отслеживаем изменения в фильтрованных данных для показа индикатора
    filteredNavarea() {
      // Если началась фильтрация и есть данные для расчета
      // Показываем индикатор только для больших наборов данных (>10 элементов)
      if (this.isFilteringActive && this.hasRouteData && this.isComponentActive && 
        this.navareaData.length > 10 && this.routeIntersectionCache.size === 0) {
        this.startIntersectionCalculation();
      }
    }
  },
  computed: {
    isFilteringActive() {
      return this.effectiveBufferDistanceNm > 0 && this.hasRouteData;
    },
    hasPendingFilter() {
      return this.bufferDistanceNm > 0 && this.bufferDistanceNm !== this.effectiveBufferDistanceNm;
    },
    bufferDistanceDisplay() {
      return this.formatDistanceLabel(this.bufferDistanceNm);
    },
    effectiveBufferDistanceDisplay() {
      return this.formatDistanceLabel(this.effectiveBufferDistanceNm);
    },
    // Получаем данные Navarea
    ...mapState(useFleetStore, {
      navareaData: state => state.data.navarea || []
    }),
    // Получаем данные маршрута
    ...mapState(useRoutesStore, {
      routeWaypoints: state => state.getShipWaypoints || []
    }),

    // Преобразуем новый формат в плоский массив сообщений
    allNavareMessages() {
      console.log('🔍 NavareaTable: Raw navareaData:', this.navareaData);
      
      if (!this.navareaData) {
        console.log('❌ NavareaTable: navareaData is null/undefined');
        return [];
      }

      // Проверяем, пришли ли данные в новом формате (объект с results)
      if (this.navareaData.results && Array.isArray(this.navareaData.results)) {
        console.log('📊 NavareaTable: Data is in NEW API FORMAT (object with results), processing...');
        const messages = [];
        this.navareaData.results.forEach(countryData => {
          if (countryData.active_messages && countryData.active_messages.length > 0) {
            countryData.active_messages.forEach(message => {
              messages.push({
                id: message.id,
                number: message.number,
                content: message.content,
                country: countryData.country,
                type: countryData.type,
                time_upd: countryData.time_upd,
                coordinates: message.coordinates,
                uniqueKey: `${countryData.country}_${message.number}` // Уникальный ключ для таблицы
              });
            });
          }
        });
        console.log('✅ NavareaTable: Processed new API format data count:', messages.length);
        console.log('📋 NavareaTable: Sample new API format item:', messages[0]);
        return messages;
      }

      // Проверяем, если данные - это массив в старом формате
      if (Array.isArray(this.navareaData) && this.navareaData.length > 0 && this.navareaData[0].id1) {
        console.log('📊 NavareaTable: Data is in OLD FORMAT, converting...');
        const converted = this.navareaData.map((item, index) => ({
          ...item,
          // Конвертируем старые поля в новые для совместимости
          number: item.id1,
          content: item.message,
          coordinates: {
            decimal_coordinates: item.locationPoints || [],
            geojson_coordinates: item.locationAreas || []
          },
          uniqueKey: `${item.id1}_${item.area}_${index}` // Уникальный ключ для избежания дублирования
        }));
        console.log('✅ NavareaTable: Converted old format data count:', converted.length);
        console.log('📋 NavareaTable: Sample converted item:', converted[0]);
        return converted;
      }

      // Проверяем, если данные - это массив в новом формате (прямой массив стран)
      if (Array.isArray(this.navareaData)) {
        console.log('📊 NavareaTable: Data is in NEW FORMAT (array), processing...');
        const messages = [];
        this.navareaData.forEach(countryData => {
          if (countryData.active_messages && countryData.active_messages.length > 0) {
            countryData.active_messages.forEach(message => {
              messages.push({
                id: message.id,
                number: message.number,
                content: message.content,
                country: countryData.country,
                type: countryData.type,
                time_upd: countryData.time_upd,
                coordinates: message.coordinates,
                uniqueKey: `${countryData.country}_${message.number}` // Уникальный ключ для таблицы
              });
            });
          }
        });
        console.log('✅ NavareaTable: Processed new format data count:', messages.length);
        console.log('📋 NavareaTable: Sample new format item:', messages[0]);
        return messages;
      }

      console.log('❌ NavareaTable: Unknown data format:', typeof this.navareaData, this.navareaData);
      return [];
    },

    // Проверяем наличие данных маршрута
    hasRouteData() {
      return this.routeWaypoints && this.routeWaypoints.length > 0;
    },    
    // Получаем координаты маршрута для расчета пересечений
    routeCoordinates() {
      if (!this.hasRouteData) return [];
      
      return this.routeWaypoints.map(point => [
        parseFloat(point.lat),
        parseFloat(point.lon)
      ]).filter(coord => !isNaN(coord[0]) && !isNaN(coord[1]));
    },    
    // Вычисляем границы маршрута для быстрой предварительной проверки
    routeBounds() {
      if (!this.routeCoordinates || this.routeCoordinates.length === 0) {
        return null;
      }

      // Создаем простой хэш для маршрута
      const routeHash = this.routeCoordinates.map(coord => `${coord[0].toFixed(6)},${coord[1].toFixed(6)}`).join('|');
      
      // Если маршрут не изменился, возвращаем кэшированные границы
      if (this.lastRouteHash === routeHash && this.cachedRouteBounds) {
        return this.cachedRouteBounds;
      }
      
      let minLat = Infinity, maxLat = -Infinity;
      let minLon = Infinity, maxLon = -Infinity;
      
      this.routeCoordinates.forEach(([lat, lon]) => {
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
        minLon = Math.min(minLon, lon);
        maxLon = Math.max(maxLon, lon);
      });
      
      // Добавляем небольшой буфер для учета погрешностей
      const buffer = 0.01; // ~1км
      const bounds = {
        minLat: minLat - buffer,
        maxLat: maxLat + buffer,
        minLon: minLon - buffer,
        maxLon: maxLon + buffer
      };

      // Кэшируем результат
      this.lastRouteHash = routeHash;
      this.cachedRouteBounds = bounds;
      
      return bounds;
    },

    routeBuffer() {
      if (!this.hasRouteData || this.effectiveBufferDistanceNm <= 0) {
        return null;
      }

      try {
        const routeCoords = this.routeWaypoints
          .map(point => [parseFloat(point.lon), parseFloat(point.lat)])
          .filter(coord => Array.isArray(coord) && coord.every(value => Number.isFinite(value)));

        if (routeCoords.length < 2) {
          return null;
        }

        const bufferDistance = this.effectiveBufferDistanceNm;
        const routeLine = turf.lineString(routeCoords);
        return turf.buffer(routeLine, bufferDistance, { units: 'nauticalmiles' });
      } catch (error) {
        console.error('Failed to create route buffer:', error);
        return null;
      }
    },

    // Фильтрованные данные по пересечению с маршрутом
    filteredNavarea() {
      // Если фильтр выключен или нет данных маршрута - возвращаем все
      if (!this.isFilteringActive || !this.isComponentActive) {
        return this.allNavareMessages;
      }

      return this.allNavareMessages.filter(item => {
        return this.checkNavareaIntersectsRoute(item);
      });
    },

    formattedNavAreaData() {
      return this.filteredNavarea.map((item, index) => ({
        ...item,
        expanded: item.number === this.expandedRowId,
        uniqueKey: `${item.number}_${item.area}_${index}` // Уникальный ключ для избежания дублирования
      }));
    }
  },
  methods: {
    formatDistanceLabel(distanceNm) {
      if (!Number.isFinite(distanceNm) || distanceNm <= 0) {
        return '0';
      }

      const precision = distanceNm < 10 ? 1 : 0;
      return distanceNm
        .toFixed(precision)
        .replace(/\.0$/, '');
    },
    scheduleBufferUpdate(newDistance) {
      const normalizedDistance = Number.isFinite(newDistance) ? Math.max(0, newDistance) : 0;

      if (this.bufferDebounceTimer) {
        clearTimeout(this.bufferDebounceTimer);
        this.bufferDebounceTimer = null;
      }

      if (!this.isComponentActive) {
        this.applyBufferDistance(normalizedDistance);
        return;
      }

      if (normalizedDistance === 0) {
        this.applyBufferDistance(0);
        return;
      }

      this.bufferDebounceTimer = setTimeout(() => {
        this.applyBufferDistance(normalizedDistance);
        this.bufferDebounceTimer = null;
      }, 1500);
    },
    applyBufferDistance(distanceNm) {
      const sanitizedDistance = Number.isFinite(distanceNm)
        ? Math.max(0, Math.min(distanceNm, 20))
        : 0;

      if (this.effectiveBufferDistanceNm === sanitizedDistance) {
        return;
      }

      this.effectiveBufferDistanceNm = sanitizedDistance;
      this.onFilterChange();

      if (sanitizedDistance === 0) {
        this.endIntersectionCalculation();
      }
    },
    truncateMessage(message) {
      if (message && message.length > 100) {
        return message.substring(0, 100) + '...';
      }
      return message;
    },
    expandRow(id) {
      this.expandedRowId = id;
    },
    collapseRow() {
      this.expandedRowId = null;
    },
    // Обработчик клика по строкам таблицы
    handleRowClick(item) {
      this.selectedItemId = item.number;
      
      // Эмитим событие для показа объекта на карте
      eventBus.$emit('show-navarea-on-map', item);
      
      // Эмитим событие для выделения объекта на карте
      eventBus.$emit('highlight-navarea-on-map', item);
    },    // Выделение строки в таблице при клике по объекту на карте
    highlightItem(navareaItem) {
      const itemId = navareaItem.number || navareaItem.id || navareaItem;
      this.selectedItemId = itemId;
      
      // Найти элемент в фильтрованных данных
      const itemIndex = this.filteredNavarea.findIndex(item => item.number === itemId);
      if (itemIndex !== -1) {
        // Прокрутить таблицу к нужной строке
        this.$nextTick(() => {
          const table = this.$el.querySelector('.navarea-data-table');
          if (table) {
            const row = table.querySelector(`tbody tr:nth-child(${itemIndex + 1})`);
            if (row) {
              row.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center' 
              });
            }
          }
        });
        
        // Выделяем соответствующие элементы на карте
        const selectedItem = this.filteredNavarea[itemIndex];
        eventBus.$emit('highlight-navarea-on-map', {
          number: selectedItem.number,
          area: selectedItem.area,
          content: selectedItem.content,
          coordinates: selectedItem.coordinates
        });
      } else {
        // Если элемент не найден, сбрасываем выделение
        eventBus.$emit('clear-navarea-highlight');
      }
    },

    // CSS класс для выбранной строки
    getRowClass(item, type) {
      if (type === 'row' && item && item.number === this.selectedItemId) {
        return 'table-info';
      }
      return '';
    },    // При изменении фильтра
    onFilterChange() {
      // Сбрасываем кэш, чтобы пересчитать пересечения с актуальным буфером
      this.routeIntersectionCache.clear();
      this.cachedRouteBounds = null;
      this.lastRouteHash = null;
    },

    // Debounced очистка кэша для избежания множественных очисток при быстрых изменениях маршрута
    debounceCacheClear() {
      // Отменяем предыдущий таймер если он есть
      if (this.cacheClearTimeout) {
        clearTimeout(this.cacheClearTimeout);
      }
      // Устанавливаем новый таймер
      this.cacheClearTimeout = setTimeout(() => {
        this.routeIntersectionCache.clear();
        // Также очищаем кэш границ маршрута
        this.cachedRouteBounds = null;
        this.lastRouteHash = null;
        this.cacheClearTimeout = null;
      }, 300); // 300ms debounce - достаточно для избежания лишних вызовов
    },

    // Методы для управления индикатором расчета пересечений
    startIntersectionCalculation() {
      if (this.calculationTimer) {
        clearTimeout(this.calculationTimer);
      }
      
      if (!this.isCalculatingIntersections) {
        this.isCalculatingIntersections = true;
        eventBus.$emit('intersection-calculation-start');
      }
      // Задержка для завершения расчета (debounce)
      this.calculationTimer = setTimeout(() => {
        this.endIntersectionCalculation();
      }, 300);
    },
    
    endIntersectionCalculation() {
      if (this.isCalculatingIntersections) {
        this.isCalculatingIntersections = false;
        eventBus.$emit('intersection-calculation-end');
      }
      
      if (this.calculationTimer) {
        clearTimeout(this.calculationTimer);
        this.calculationTimer = null;
      }
    },

    // Быстрая проверка пересечения Bounding Box (AABB - Axis-Aligned Bounding Box)
    checkBoundingBoxIntersection(itemBounds, routeBounds) {
      if (!itemBounds || !routeBounds) return false;
      
      return !(itemBounds.maxLon < routeBounds.minLon ||
        itemBounds.minLon > routeBounds.maxLon ||
        itemBounds.maxLat < routeBounds.minLat ||
        itemBounds.minLat > routeBounds.maxLat);
    },

    // Получаем границы Navarea объекта
    getNavareaItemBounds(navareaItem) {
      if (!navareaItem?.coordinates) {
        return null;
      }

      const source = this.resolveCoordinateSource(navareaItem.coordinates);
      if (!source) {
        return null;
      }

      const { coords, format } = source;
      let minLat = Infinity;
      let maxLat = -Infinity;
      let minLon = Infinity;
      let maxLon = -Infinity;
      let hasValidCoords = false;

      coords.forEach(coord => {
        const normalized = this.coordsToArray(coord, format);
        if (normalized) {
          const [lon, lat] = normalized;
          minLat = Math.min(minLat, lat);
          maxLat = Math.max(maxLat, lat);
          minLon = Math.min(minLon, lon);
          maxLon = Math.max(maxLon, lon);
          hasValidCoords = true;
        }
      });

      if (!hasValidCoords) {
        return null;
      }

      return {
        minLat,
        maxLat,
        minLon,
        maxLon,
      };
    },

    // Проверка, пересекается ли Navarea объект с маршрутом
    checkNavareaIntersectsRoute(navareaItem) {
      if (!this.hasRouteData || !navareaItem?.coordinates) {
        return false;
      }

      const cacheKey = navareaItem.uniqueKey;
      if (cacheKey && this.routeIntersectionCache.has(cacheKey)) {
        return this.routeIntersectionCache.get(cacheKey);
      }

      try {
        const bufferedRoute = this.routeBuffer;
        if (!bufferedRoute) {
          if (cacheKey) {
            this.routeIntersectionCache.set(cacheKey, false);
          }
          return false;
        }

        const source = this.resolveCoordinateSource(navareaItem.coordinates);
        if (!source) {
          if (cacheKey) {
            this.routeIntersectionCache.set(cacheKey, false);
          }
          return false;
        }

        const { coords, format } = source;
        const normalized = coords
          .map(coord => this.coordsToArray(coord, format))
          .filter(coord => Array.isArray(coord) && coord.every(value => !Number.isNaN(value)));

        if (normalized.length === 0) {
          if (cacheKey) {
            this.routeIntersectionCache.set(cacheKey, false);
          }
          return false;
        }

        let intersects = false;

        if (normalized.length === 1) {
          intersects = turf.booleanPointInPolygon(turf.point(normalized[0]), bufferedRoute);
        } else {
          const polygonRing = this.getPolygonRing(normalized);

          if (polygonRing) {
            const polygon = turf.polygon([polygonRing]);
            intersects =
              turf.booleanIntersects(polygon, bufferedRoute) ||
              turf.booleanContains(bufferedRoute, polygon) ||
              turf.booleanContains(polygon, bufferedRoute);
          }

          if (!intersects) {
            const navareaLine = turf.lineString(normalized);
            intersects = turf.booleanIntersects(navareaLine, bufferedRoute);
          }

          if (!intersects) {
            intersects = normalized.some(coord =>
              turf.booleanPointInPolygon(turf.point(coord), bufferedRoute)
            );
          }
        }

        if (cacheKey) {
          this.routeIntersectionCache.set(cacheKey, intersects);
        }

        return intersects;
      } catch (error) {
        console.warn('Error checking navarea intersection:', error);
        if (cacheKey) {
          this.routeIntersectionCache.set(cacheKey, false);
        }
        return false;
      }
    },

    resolveCoordinateSource(coordinates) {
      if (!coordinates) {
        return null;
      }

      if (Array.isArray(coordinates.geojson_coordinates) && coordinates.geojson_coordinates.length) {
        return { coords: coordinates.geojson_coordinates, format: 'geojson' };
      }

      if (Array.isArray(coordinates.decimal_coordinates) && coordinates.decimal_coordinates.length) {
        return { coords: coordinates.decimal_coordinates, format: 'decimal' };
      }

      if (coordinates.lat !== undefined && coordinates.lon !== undefined) {
        return { coords: [{ lat: coordinates.lat, lon: coordinates.lon }], format: 'object' };
      }

      return null;
    },

    normalizeCoordinate(coord, format = 'auto') {
      if (!coord) {
        return null;
      }

      let lat;
      let lon;

      if (Array.isArray(coord)) {
        const first = parseFloat(coord[0]);
        const second = parseFloat(coord[1]);

        if (Number.isNaN(first) || Number.isNaN(second)) {
          return null;
        }

        if (format === 'decimal') {
          lat = first;
          lon = second;
        } else if (format === 'geojson') {
          lon = first;
          lat = second;
        } else if (format === 'object') {
          lat = first;
          lon = second;
        } else {
          const looksLatLon = Math.abs(first) <= 90 && Math.abs(second) <= 180;
          const looksLonLat = Math.abs(first) <= 180 && Math.abs(second) <= 90;

          if (looksLatLon && !looksLonLat) {
            lat = first;
            lon = second;
          } else if (looksLonLat && !looksLatLon) {
            lon = first;
            lat = second;
          } else if (Math.abs(second) > 90 && Math.abs(first) <= 90) {
            lat = first;
            lon = second;
          } else {
            lon = first;
            lat = second;
          }
        }
      } else if (typeof coord === 'object') {
        lat = parseFloat(coord.lat);
        lon = parseFloat(coord.lon);
      } else {
        return null;
      }

      if (Number.isNaN(lat) || Number.isNaN(lon)) {
        return null;
      }

      return [lon, lat];
    },

    getPolygonRing(coords) {
      if (!coords || coords.length < 4) {
        return null;
      }

      const [firstLon, firstLat] = coords[0];
      const [lastLon, lastLat] = coords[coords.length - 1];

      if (Math.abs(firstLon - lastLon) < 1e-6 && Math.abs(firstLat - lastLat) < 1e-6) {
        return coords;
      }

      return null;
    },

    // Преобразование координат в GeoJSON точку
    coordsToGeoJSON(coords, format = 'auto') {
      const normalized = this.normalizeCoordinate(coords, format);
      if (!normalized) {
        return null;
      }

      return turf.point(normalized);
    },

    // Преобразование координат в массив [lon, lat]
    coordsToArray(coords, format = 'auto') {
      return this.normalizeCoordinate(coords, format);
    },


    
    // Проверяет пересечение маршрута с полигоном через Turf.js
    checkRoutePolygonIntersection(routeCoords, polygonCoords) {
      if (!routeCoords || !polygonCoords || routeCoords.length < 2 || polygonCoords.length < 3) {
        return false;
      }

      try {
        // Убедимся, что координаты - валидные числа
        const validRouteCoords = routeCoords.filter(coord => 
          !isNaN(coord[0]) && !isNaN(coord[1]) && 
          Math.abs(coord[0]) <= 90 && Math.abs(coord[1]) <= 180
        );
        
        const validPolygonCoords = polygonCoords.filter(coord => 
          !isNaN(coord[0]) && !isNaN(coord[1]) && 
          Math.abs(coord[0]) <= 90 && Math.abs(coord[1]) <= 180
        );
        
        if (validRouteCoords.length < 2 || validPolygonCoords.length < 3) {
          return false;
        }

        // БЫСТРАЯ ПРЕДВАРИТЕЛЬНАЯ ПРОВЕРКА: Bounding Box для полигона
        const polygonBounds = this.getBoundsFromCoords(validPolygonCoords);
        if (!this.checkBoundingBoxIntersection(polygonBounds, this.routeBounds)) {
          return false;
        }
        
        // Создаем LineString из координат маршрута для Turf.js (lng, lat формат)
        const routeLine = turf.lineString(validRouteCoords.map(coord => [coord[1], coord[0]]));
        
        // Создаем Polygon из координат Navarea объекта для Turf.js и убеждаемся, что полигон замкнут
        const polygonForTurf = [...validPolygonCoords];
        if (polygonForTurf[0][0] !== polygonForTurf[polygonForTurf.length - 1][0] || 
          polygonForTurf[0][1] !== polygonForTurf[polygonForTurf.length - 1][1]) {
          polygonForTurf.push(polygonForTurf[0]); // Замыкаем полигон
        }
        
        // Создаем полигон и проверяем пересечение
        try {
          const navareaPolygon = turf.polygon([[...polygonForTurf.map(coord => [coord[1], coord[0]])]]);
          
          // Проверяем пересечение линии с полигоном
          return turf.booleanIntersects(routeLine, navareaPolygon);
        } catch (error) {
          // Возможно, полигон не валидный - пробуем создать буфер вокруг линии и проверить пересечение с точками
          const bufferedRoute = turf.buffer(routeLine, 0.01, { units: 'degrees' });
          
          // Проверяем, находится ли хотя бы одна точка полигона внутри буфера маршрута
          for (const coord of validPolygonCoords) {
            const point = turf.point([coord[1], coord[0]]);
            if (turf.booleanPointInPolygon(point, bufferedRoute)) {
              return true;
            }
          }
          
          // Если все не сработало, используем резервный метод
          return this.checkRoutePolygonIntersectionSimple(validRouteCoords, validPolygonCoords);
        }
      } catch (error) {
        // Fallback на простой алгоритм при ошибке в Turf.js
        return this.checkRoutePolygonIntersectionSimple(routeCoords, polygonCoords);
      }
    },

    // Вспомогательный метод для получения границ из массива координат
    getBoundsFromCoords(coords) {
      if (!coords || coords.length === 0) return null;
      
      let minLat = Infinity, maxLat = -Infinity;
      let minLon = Infinity, maxLon = -Infinity;
      
      coords.forEach(([lat, lon]) => {
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
        minLon = Math.min(minLon, lon);
        maxLon = Math.max(maxLon, lon);
      });
      
      return { minLat, maxLat, minLon, maxLon };
    },// Простой алгоритм проверки пересечения как fallback
    checkRoutePolygonIntersectionSimple(routeCoords, polygonCoords) {
      // Проверяем, находится ли хотя бы одна точка маршрута внутри полигона
      for (const point of routeCoords) {
        if (this.pointInPolygon(point, polygonCoords)) {
          return true;
        }
      }
      
      // Проверяем пересечение каждого сегмента маршрута с полигоном
      for (let i = 0; i < routeCoords.length - 1; i++) {
        const segmentStart = routeCoords[i];
        const segmentEnd = routeCoords[i + 1];
        
        // Проверяем, пересекает ли сегмент маршрута полигон
        if (this.lineIntersectsPolygon(segmentStart, segmentEnd, polygonCoords)) {
          return true;
        }
      }
      
      // Проверяем, находится ли хотя бы одна точка полигона внутри буфера маршрута
      // (для случаев, когда полигон находится целиком внутри маршрута)
      if (this.routeBuffer) {
        for (const point of polygonCoords) {
          const turfPoint = turf.point([point[1], point[0]]);
          if (turf.booleanPointInPolygon(turfPoint, this.routeBuffer)) {
            return true;
          }
        }
      }

      return false;
    },

    // Проверяем пересечение линии с полигоном
    lineIntersectsPolygon(lineStart, lineEnd, polygonCoords) {
      // Проверяем, находятся ли концы линии внутри полигона
      if (this.pointInPolygon(lineStart, polygonCoords) || 
        this.pointInPolygon(lineEnd, polygonCoords)) {
        return true;
      }

      // Проверяем пересечение линии с ребрами полигона
      for (let i = 0; i < polygonCoords.length; i++) {
        const edgeStart = polygonCoords[i];
        const edgeEnd = polygonCoords[(i + 1) % polygonCoords.length];
        
        if (this.linesIntersect(lineStart, lineEnd, edgeStart, edgeEnd)) {
          return true;
        }
      }

      return false;
    },

    // Проверяет, находится ли точка внутри полигона (алгоритм ray casting)
    pointInPolygon(point, polygonCoords) {
      const [x, y] = point;
      let inside = false;

      for (let i = 0, j = polygonCoords.length - 1; i < polygonCoords.length; j = i++) {
        const [xi, yi] = polygonCoords[i];
        const [xj, yj] = polygonCoords[j];

        if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
          inside = !inside;
        }
      }

      return inside;
    },

    // Проверяем пересечение двух линий
    linesIntersect(line1Start, line1End, line2Start, line2End) {
      const [x1, y1] = line1Start;
      const [x2, y2] = line1End;
      const [x3, y3] = line2Start;
      const [x4, y4] = line2End;

      const denominator = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
      
      if (Math.abs(denominator) < 1e-10) {
        return false; // Линии параллельны
      }

      const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denominator;
      const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denominator;
      
      return t >= 0 && t <= 1 && u >= 0 && u <= 1;
    }
  }
}
</script>

<style scoped>
.navarea-table-container {
  width: 100%;
  padding: 0px;
  font-size: 0.9rem;
}

.navarea-filter-controls {
  padding: 8px 12px;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  margin-bottom: 8px;
}

.navarea-filter-controls .form-check {
  margin-bottom: 0;
}

.navarea-distance-slider {
  flex: 0 0 220px;
  max-width: 220px;
}

.section-title {
  font-size: 1rem;
  margin-bottom: 5px;
  color: #555;
}

.navarea-data-table {
  max-height: 400px;
  overflow-y: auto;
  font-size: 0.85rem;
}

.no-data {
  color: #888;
  font-style: italic;
  font-size: 0.85rem;
  margin-top: 5px;
}

/* Стиль для строк таблицы */
:deep(.table) tr {
  cursor: pointer;
  transition: background-color 0.2s;
}

:deep(.table) tr:hover {
  background-color: #f0f8ff;
}

/* Выделенная строка */
:deep(.table-info) {
  background-color: #d1ecf1 !important;
}

/* Стили для мобильной версии */
@media (max-width: 768px) {
  .navarea-table-container {
    padding: 0px;
  }

  .section-title {
    font-size: 0.9rem;
  }

  .navarea-distance-slider {
    flex: 1 1 auto;
    max-width: none;
  }

  .navarea-data-table {
    font-size: 0.8rem;
  }
}

:deep(tr.highlighted-row) {
  background-color: #ffee88 !important;
  transition: background-color 0.3s;
}
</style>